// Miembros: las personas de la agencia con acceso al panel. Solo el propietario añade y quita.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { api, type User } from '../../api.ts';
import { t } from '../../i18n/index.ts';
import { errorText, useApp } from '../../store.ts';
import { ago } from '../../format.ts';
import { MenuButton } from '../../ui/Menu.tsx';
import { Dialog } from '../../ui/Dialog.tsx';

export function Members() {
  const me = useApp((s) => s.me);
  const notify = useApp((s) => s.notify);
  const [users, setUsers] = useState<User[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'member' as 'owner' | 'member' });

  const load = useCallback(async () => setUsers((await api.users()).users), []);
  useEffect(() => {
    load().catch((e) => notify(errorText(e), true));
  }, [load, notify]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.addUser(form);
      setAdding(false);
      setForm({ name: '', email: '', password: '', role: 'member' });
      await load();
      notify(t('members.added'));
    } catch (err) {
      notify(errorText(err), true);
    }
  };

  const owner = me?.role === 'owner';
  return (
    <section className="section">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2>{t('ws.members')}</h2>
        {owner ? (
          <button type="button" className="btn primary" onClick={() => setAdding(true)}>
            {t('members.add')}
          </button>
        ) : null}
      </div>
      <div className="list">
        {users.map((u) => (
          <div key={u.id} className="list-row">
            <div className="col grow" style={{ gap: 4 }}>
              <span className="ellipsis" title={u.email}>
                {u.name} <span className="muted">· {u.email}</span>
              </span>
              <span className="hint">{t('members.lastLogin', { when: ago(u.lastLoginAt) })}</span>
            </div>
            {u.totpEnabled ? <span className="badge green">2FA</span> : <span className="badge amber">{t('members.no2fa')}</span>}
            <span className="badge">{u.role === 'owner' ? t('members.owner') : t('members.member')}</span>
            {owner && u.id !== me?.id ? (
              <MenuButton
                align="end"
                title={t('common.more')}
                items={[
                  {
                    label: t('members.remove'),
                    danger: true,
                    onSelect: async () => {
                      try {
                        await api.deleteUser(u.id);
                        await load();
                      } catch (err) {
                        notify(errorText(err), true);
                      }
                    },
                  },
                ]}
              >
                <MoreHorizontal size={14} />
              </MenuButton>
            ) : null}
          </div>
        ))}
      </div>
      {adding ? (
        <Dialog title={t('members.add')} onClose={() => setAdding(false)}>
          <form className="col" onSubmit={add}>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('account.name')} required maxLength={80} autoFocus />
            <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('account.email')} required />
            <input className="input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t('members.tempPassword')} minLength={10} required autoComplete="new-password" />
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as 'owner' | 'member' })}>
              <option value="member">{t('members.member')}</option>
              <option value="owner">{t('members.owner')}</option>
            </select>
            <div className="dialog-foot">
              <button type="button" className="btn" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn primary">
                {t('members.add')}
              </button>
            </div>
          </form>
        </Dialog>
      ) : null}
    </section>
  );
}
